import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Save } from "lucide-react";
import Seo from "../components/Seo";
import { getProfile, updateProfile } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { LOCATIONS } from "../lib/content";

const WB = LOCATIONS["west-branch"];
const EDITABLE = [
  ["firstName", "First name", "given-name"],
  ["lastName", "Last name", "family-name"],
  ["phone", "Mobile number", "tel"],
];
// The vendor's profile now carries an address. It is optional, but a PARTIAL
// address is rejected, so these four travel together.
const ADDRESS = [
  ["address1", "Street address", "address-line1", true],
  ["address2", "Address 2 (optional)", "address-line2", false],
  ["city", "City", "address-level2", true],
  ["state", "State (2 letters)", "address-level1", true],
  ["postalCode", "ZIP code", "postal-code", true],
];

export default function AccountProfile() {
  const { signedIn, ready, logout } = useAuth();
  const [form, setForm] = useState(null);
  const [state, setState] = useState({ busy: false, error: null, saved: false });

  useEffect(() => {
    if (!ready || !signedIn) return;
    getProfile()
      .then((p) => setForm(p))
      .catch((err) => setState({ busy: false, error: err.payload?.userMessage || err.message, saved: false }));
  }, [ready, signedIn]);

  const submit = async (e) => {
    e.preventDefault();
    const required = ADDRESS.filter(([, , , req]) => req).map(([n]) => n);
    const filled = required.filter((n) => String(form[n] || "").trim());
    if (filled.length && filled.length !== required.length) {
      return setState({
        busy: false, saved: false,
        error: "Please fill in your street address, city, state and ZIP code together — a partial "
             + "address can't be saved.",
      });
    }
    setState({ busy: true, error: null, saved: false });
    try {
      const saved = await updateProfile({
        firstName: form.firstName, lastName: form.lastName, phone: form.phone,
        ...Object.fromEntries(
          ADDRESS.map(([n]) => [n, String(form[n] || "").trim()]).filter(([, v]) => v),
        ),
        ...(filled.length ? { country: form.country || "US" } : {}),
      });
      setForm(saved);
      setState({ busy: false, error: null, saved: true });
    } catch (err) {
      setState({ busy: false, error: err.payload?.userMessage || err.message, saved: false });
    }
  };

  if (ready && !signedIn) {
    return (
      <div data-testid="account-signed-out" className="mx-auto max-w-md px-5 py-32 text-center lg:px-10">
        <h1 className="font-display text-3xl font-extrabold text-navy">Please sign in</h1>
        <Link to="/account/login?next=/account" className="mt-6 inline-block rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover">
          Sign in
        </Link>
      </div>
    );
  }

  return (
    <>
      <Seo title="Your details | West Branch Marina" path="/account" noindex />
      <div className="mx-auto max-w-2xl px-5 py-28 lg:px-10">
        <h1 className="font-display text-4xl font-extrabold tracking-tight text-navy">Your details</h1>
        <p className="mt-3 text-sm text-slate-600">
          These are used to prefill your bookings. Your email is your sign-in and can't be changed here —
          call us on{" "}
          <a href={WB.phoneHref} className="font-bold text-lake underline">{WB.phone}</a> if it needs updating.
        </p>

        {form && (
          <form data-testid="profile-form" onSubmit={submit} className="mt-8 grid gap-4 sm:grid-cols-2" noValidate>
            {state.error && (
              <p data-testid="profile-error" role="alert" aria-live="assertive" className="sm:col-span-2 rounded-xl border border-red-300 bg-red-50 p-3 text-sm text-red-800">
                {state.error}
              </p>
            )}
            {state.saved && (
              <p data-testid="profile-saved" role="status" aria-live="polite" className="sm:col-span-2 rounded-xl border border-lake/40 bg-lake/[0.07] p-3 text-sm text-navy">
                Saved.
              </p>
            )}
            {EDITABLE.map(([name, label, autoComplete]) => (
              <div key={name}>
                <label htmlFor={`profile-${name}`} className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                  {label}
                </label>
                <input
                  id={`profile-${name}`}
                  data-testid={`profile-${name}`}
                  autoComplete={autoComplete}
                  required
                  value={form[name] ?? ""}
                  onChange={(e) => setForm({ ...form, [name]: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm focus:border-lake focus:outline-none focus:ring-2 focus:ring-lake/30"
                />
              </div>
            ))}
            <div className="sm:col-span-2">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-600">Email</p>
              <p data-testid="profile-email" className="mt-2 text-sm text-slate-700">{form.email}</p>
            </div>

            <div className="sm:col-span-2 border-t border-sand-border pt-5">
              <h2 className="font-display text-lg font-bold text-navy">Your address</h2>
              <p className="mt-1 text-sm text-slate-600">
                Saved once and used to prefill your checkout. Street address, city, state and ZIP must
                be filled in together.
              </p>
            </div>
            {ADDRESS.map(([name, label, autoComplete, required]) => (
              <div key={name} className={name === "address1" || name === "address2" ? "sm:col-span-2" : undefined}>
                <label htmlFor={`profile-${name}`} className="block text-xs font-bold uppercase tracking-wider text-slate-600">
                  {label}
                </label>
                <input
                  id={`profile-${name}`}
                  data-testid={`profile-${name}`}
                  autoComplete={autoComplete}
                  aria-required={required}
                  value={form[name] ?? ""}
                  onChange={(e) => setForm({ ...form, [name]: e.target.value })}
                  className="mt-2 w-full rounded-xl border border-navy/15 px-4 py-3 text-sm focus:border-lake focus:outline-none focus:ring-2 focus:ring-lake/30"
                />
              </div>
            ))}
            <button
              type="submit"
              data-testid="profile-save"
              disabled={state.busy}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover disabled:opacity-60"
            >
              <Save className="h-4 w-4" aria-hidden="true" />
              {state.busy ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              data-testid="profile-signout"
              onClick={logout}
              className="rounded-full border border-navy/20 px-7 py-3.5 font-bold text-navy hover:bg-sand-card"
            >
              Sign out
            </button>
          </form>
        )}
      </div>
    </>
  );
}
